import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { IExpenseCategory } from 'core/hooks';

import * as S from '../FakturowniaInvoices/FakturowniaInvoices.styled';
import { IExpensesView, IGroupOption, TStatusSeg } from './filtering';

interface IExpensesFilterBarProps {
  view: IExpensesView;
  onChange: (next: IExpensesView) => void;
  onReset: () => void;
  categories: IExpenseCategory[];
  groupOptions: IGroupOption[];
  /** Raw search box value (debounced into `view.search` by the page). */
  searchInput: string;
  onSearchInput: (value: string) => void;
}

const SEGMENTS: { value: TStatusSeg; i18n: string }[] = [
  { value: 'all', i18n: 'segAll' },
  { value: 'toPay', i18n: 'segToPay' },
  { value: 'paid', i18n: 'segPaid' },
  { value: 'cancelled', i18n: 'segCancelled' },
];

const SOURCE_OPTIONS = [
  { value: '', i18n: 'sourceAll' },
  { value: 'fakturownia', i18n: 'sourceFakturownia' },
  { value: 'manual', i18n: 'sourceManual' },
] as const;

// Wire values are lowercase to match the (lower-cased) row recurrence.
const RECURRENCE_OPTIONS = [
  { value: '', i18n: 'recurrenceAll' },
  { value: 'none', i18n: 'recurrenceNone' },
  { value: 'weekly', i18n: 'recurrenceWeekly' },
  { value: 'monthly', i18n: 'recurrenceMonthly' },
] as const;

/** Filter ribbon for the Expenses register — segment (paid status) + search +
 *  group/source/category/recurrence selects, all applied client-side. */
export const ExpensesFilterBar = memo(
  ({
    view,
    onChange,
    onReset,
    categories,
    groupOptions,
    searchInput,
    onSearchInput,
  }: IExpensesFilterBarProps) => {
    const { t } = useTranslation();

    return (
      <S.FilterBar>
        <S.SegmentTabs>
          {SEGMENTS.map((seg) => (
            <S.SegmentTab
              key={seg.value}
              type="button"
              active={view.statusSeg === seg.value}
              onClick={() => onChange({ ...view, statusSeg: seg.value })}
            >
              {t(`pages.fakturownia.expenses.${seg.i18n}`)}
            </S.SegmentTab>
          ))}
        </S.SegmentTabs>

        <S.FilterGroup>
          <S.NativeInput
            type="search"
            value={searchInput}
            onChange={(e) => onSearchInput(e.target.value)}
            placeholder={t('pages.fakturownia.expenses.searchPlaceholder')}
          />
        </S.FilterGroup>

        <S.FilterGroup>
          <S.FilterLabel>
            {t('pages.fakturownia.expenses.filters.group')}
          </S.FilterLabel>
          <S.NativeSelect
            value={view.groupId}
            onChange={(e) => onChange({ ...view, groupId: e.target.value })}
          >
            <option value="">
              {t('pages.fakturownia.expenses.filters.groupAll')}
            </option>
            <option value="none">
              {t('pages.fakturownia.expenses.filters.groupNone')}
            </option>
            {groupOptions.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </S.NativeSelect>
        </S.FilterGroup>

        <S.FilterGroup>
          <S.FilterLabel>
            {t('pages.fakturownia.expenses.filters.source')}
          </S.FilterLabel>
          <S.NativeSelect
            value={view.source}
            onChange={(e) => onChange({ ...view, source: e.target.value })}
          >
            {SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {t(`pages.fakturownia.expenses.filters.${o.i18n}`)}
              </option>
            ))}
          </S.NativeSelect>
        </S.FilterGroup>

        <S.FilterGroup>
          <S.FilterLabel>
            {t('pages.fakturownia.expenses.filters.category')}
          </S.FilterLabel>
          <S.NativeSelect
            value={view.categorySlug}
            onChange={(e) =>
              onChange({ ...view, categorySlug: e.target.value })
            }
          >
            <option value="">
              {t('pages.fakturownia.expenses.filters.categoryAll')}
            </option>
            {categories.map((c) => (
              <option key={c.id} value={c.slug}>
                {c.name}
              </option>
            ))}
          </S.NativeSelect>
        </S.FilterGroup>

        <S.FilterGroup>
          <S.FilterLabel>
            {t('pages.fakturownia.expenses.filters.recurrence')}
          </S.FilterLabel>
          <S.NativeSelect
            value={view.recurrence}
            onChange={(e) => onChange({ ...view, recurrence: e.target.value })}
          >
            {RECURRENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {t(`pages.fakturownia.expenses.filters.${o.i18n}`)}
              </option>
            ))}
          </S.NativeSelect>
        </S.FilterGroup>

        <S.FilterGroup>
          <S.FilterLabel>
            <input
              type="checkbox"
              checked={view.correctionsOnly}
              onChange={(e) =>
                onChange({ ...view, correctionsOnly: e.target.checked })
              }
              style={{ marginRight: 6 }}
            />
            {t('pages.fakturownia.expenses.filters.correctionsOnly')}
          </S.FilterLabel>
        </S.FilterGroup>

        <S.ResetButton type="button" onClick={onReset}>
          {t('pages.fakturownia.expenses.filters.reset')}
        </S.ResetButton>
      </S.FilterBar>
    );
  },
);
